import { useEffect, useMemo } from 'react';
import { type FieldArrayPath, type Path, useFieldArray, useFormContext } from 'react-hook-form';
import type { DefinitionDataSection, ReportDefinition, ReportDefinitionData } from '../../types';

interface UseSortingEditorProps {
  sectionId: string;
}

export function useSortingEditor({ sectionId }: UseSortingEditorProps) {
  const { watch, setValue, control } = useFormContext<ReportDefinition>();
  const data = watch('data');
  const sections = (data?.sections || []) as ReportDefinitionData['sections'];
  const sectionIndex = sections.findIndex((s: DefinitionDataSection) => s.id === sectionId);

  // Use form state directly to ensure we have the latest data
  const definitionSection = sectionIndex !== -1 ? sections[sectionIndex] : undefined;

  // Use useFieldArray for sorting rules management
  const {
    append: appendSortRule,
    remove: removeSortRule,
    update: updateSortRule,
  } = useFieldArray({
    control,
    name: `data.sections.${sectionIndex}.sorting` as FieldArrayPath<ReportDefinition>,
    shouldUnregister: false,
  });

  // Get columns for cleanup logic
  const columns = definitionSection?.columns || [];

  // Create a stable reference for selected column IDs
  const selectedColumnIds = useMemo(() => new Set(columns.map((col) => col.id)), [columns]);

  // Clean up orphaned sorting rules (rules referencing columns that are no longer selected)
  useEffect(() => {
    if (sectionIndex === -1) return;

    const currentSorting = definitionSection?.sorting || [];
    const orphanedRules = currentSorting.filter((rule) => !selectedColumnIds.has(rule.id));

    if (orphanedRules.length > 0) {
      const updatedSorting = currentSorting.filter((rule) => selectedColumnIds.has(rule.id));
      setValue(`data.sections.${sectionIndex}.sorting` as Path<ReportDefinition>, updatedSorting, {
        shouldDirty: false,
      });
    }
    // Only run when selected columns change, not when sorting changes
  }, [selectedColumnIds, sectionIndex, setValue]);

  const handleAddSortRule = (columnId: string, direction: 'asc' | 'desc') => {
    if (sectionIndex === -1) return;
    type SortRuleType =
      ReportDefinitionData['sections'] extends Array<infer T>
        ? T extends { sorting?: Array<infer S> }
          ? S
          : never
        : never;

    const newSortRule: SortRuleType = {
      id: columnId,
      direction,
    } as SortRuleType;

    appendSortRule(newSortRule);
  };

  const handleRemoveSortRule = (sortIndex: number) => {
    if (sectionIndex === -1) return;
    removeSortRule(sortIndex);
  };

  const handleUpdateSortRule = (sortIndex: number, columnId: string, direction: 'asc' | 'desc') => {
    if (sectionIndex === -1) return;
    type SortRuleType =
      ReportDefinitionData['sections'] extends Array<infer T>
        ? T extends { sorting?: Array<infer S> }
          ? S
          : never
        : never;

    const updatedRule: SortRuleType = {
      id: columnId,
      direction,
    } as SortRuleType;

    updateSortRule(sortIndex, updatedRule);
  };

  return {
    definitionSection,
    handleAddSortRule,
    handleRemoveSortRule,
    handleUpdateSortRule,
  };
}
